import React, { useEffect, useState } from 'react'; 
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Toast, ToastContainer } from "react-bootstrap";
import { faAngleDown, faAngleUp, faExternalLinkAlt, faEye, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Button, Table, InputGroup, Spinner, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { BASE_URL } from '../../constants';
import axios from 'axios';
import RoomDetails from "../../components/RoomDetails";
import { useAuth } from '../../Context/AuthContext';
import Layout from "../../components/Layout";
import ConfirmDeleteModal from '../../components/DeleteModal';

const RoomsP = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [showAlert, setShowAlert] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [occupants, setOccupants] = useState([]);
  const [selectedOccupants, setSelectedOccupants] = useState([]);
  const [sortByFreePlaces, setSortByFreePlaces] = useState(false);
  const { user } = useAuth();
  const shelterId = user?.shelter?.id;
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const [editForm, setEditForm] = useState({
    roomNumber: "",
    occupant: [],
    isOccupied: false,
    capacity: "",
    _id: ""
  });

  const [formData, setFormData] = useState({
    roomNumber: "",
    capacity: "",
  });

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/accommodation/shelter/${shelterId}`);
      const allRooms = response.data.rooms;
      const filteredRooms = allRooms.filter((room) => {
        const roomMatches = room.roomNumber.toLowerCase().includes(searchQuery.toLowerCase());
        const occupantMatches = room.occupant?.some((o) => {
          const label = `${o.firstName || ''} ${o.lastName || ''}`.toLowerCase();
          return label.includes(searchQuery.toLowerCase()) || o.identifiant?.toLowerCase().includes(searchQuery.toLowerCase());
        });
        return roomMatches || occupantMatches;
      });
      setRooms(filteredRooms);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOccupants = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/refugees/shelter/${shelterId}`);
      const filtered = response.data.filter(u => u?.isRefugee);
      setOccupants(filtered);
    } catch (error) {
      console.error("Error fetching occupants:", error);
    }
  };

  useEffect(() => {
    if (shelterId) {
      fetchRooms();
      fetchOccupants();
    }
  }, [searchQuery]);

  const handleDeleteRoom = (roomId) => {
    setRoomToDelete(roomId);         
    setShowDeleteModal(true);      
  };

  const confirmDeleteRoom = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}/accommodation/${roomToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlertMessage("Room deleted successfully!");
      setAlertVariant("success");
      fetchRooms();
    } catch {
      setAlertMessage("Failed to delete room.");
      setAlertVariant("danger");
    } finally {
      setShowAlert(true);
      setShowDeleteModal(false);
    }
  };

  const handleEdit = (room) => {
    setEditForm(room);
    setSelectedOccupants(room.occupant?.map((o) => ({
      value: o._id,
      label: `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.identifiant
    })) || []);
    setShowEditModal(true);
  };

  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${BASE_URL}/accommodation/${editForm._id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlertMessage("Room updated successfully!");
      setAlertVariant("success");
      fetchRooms();
      setShowEditModal(false);
    } catch {
      setAlertMessage("Error updating room.");
      setAlertVariant("danger");
    } finally {
      setShowAlert(true);
    }
  };

  const handleSubmitRoom = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${BASE_URL}/accommodation/addRoom`, {
        shelterId,
        roomNumber: formData.roomNumber,
        capacity: formData.capacity,
      });
      setAlertMessage("Room added successfully!");
      setAlertVariant("success");
      setShowModalAdd(false);
      setFormData({ roomNumber: "", capacity: "" });
      fetchRooms();
    } catch {
      setAlertMessage("Error adding room.");
      setAlertVariant("danger");
    } finally {
      setShowAlert(true);
    }
  };

  const sortRoomsByFreePlaces = (rooms, desc = true) => {
    return [...rooms].sort((a, b) =>
      desc ? b.freePlaces - a.freePlaces : a.freePlaces - b.freePlaces
    );
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const sortedRooms = sortRoomsByFreePlaces(rooms, sortByFreePlaces);
  const paginatedRooms = sortedRooms.slice(indexOfFirst, indexOfLast);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center py-3">
        <h4>Rooms</h4>
        <Button variant="info" onClick={() => setShowModalAdd(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add New Room
        </Button>
      </div>

      {showAlert && (
        <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
          {alertMessage}
        </Alert>
      )}

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <FontAwesomeIcon icon={faSearch} />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search by name, ID, or room number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button variant="outline-secondary" onClick={() => setSortByFreePlaces(prev => !prev)}>
          Sort by Free Places {sortByFreePlaces ? "▲" : "▼"}
        </Button>
      </InputGroup>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : rooms.length === 0 ? (
        <div className="text-center p-4">
          <p>No rooms found matching your search.</p>
        </div>
      ) : (
        <Card className="shadow-sm">
          <Card.Body>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Room Number</th>
                  <th>Capacity</th>
                  <th>Free</th>
                  <th>Occupied</th>
                  <th>Occupants</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRooms.map((room) => (
                  <tr key={room._id}>
                    <td>{room.roomNumber}</td>
                    <td>{room.capacity}</td>
                    <td>{room.freePlaces}</td>
                    <td>{room.occupiedPlaces}</td>
                    <td>
                      {room.occupant?.length > 0
                        ? room.occupant
                            .map((o) => `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.identifiant)
                            .join(", ")
                        : "Vacant"}
                    </td>
                    <td>
                      <Button className="btn-sm me-2" variant="outline-primary" onClick={() => handleEdit(room)}>
                        <FaEdit />
                      </Button>
                      <Button className="btn-sm btn-danger" onClick={() => handleDeleteRoom(room._id)}>
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span>
                Page {currentPage} of {Math.ceil(sortedRooms.length / itemsPerPage)}
              </span>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                >
                  Previous
                </Button>{" "}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === Math.ceil(sortedRooms.length / itemsPerPage)}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteRoom}
        title="Delete Room"
        message="Are you sure you want to delete this room?"
      />
    </Layout>
  );
};

export default RoomsP;
